import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useSessionJwt } from '../../hooks/useSessionJwt';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useTimeRange } from '../../contexts/TimeRangeContext';
import { LatencyAnalysisSection } from '../../components';
import { isInDemoMode } from '../../services/demoDataService';
import { v2ServicePath } from '../../config';
import { components } from '../../utils/designTokens';
import { LOADING_MESSAGE } from '../../utils/strings';

// Mock latency data for demo mode
const MOCK_LATENCY_DATA = [
  { timestamp: '2026-02-26T00:00:00Z', p50_ms: 1820, p95_ms: 4250, p99_ms: 6100, avg_ms: 2140, trace_count: 142 },
  { timestamp: '2026-02-27T00:00:00Z', p50_ms: 1740, p95_ms: 3980, p99_ms: 5820, avg_ms: 2050, trace_count: 128 },
  { timestamp: '2026-02-28T00:00:00Z', p50_ms: 1960, p95_ms: 4510, p99_ms: 6340, avg_ms: 2280, trace_count: 156 },
  { timestamp: '2026-03-01T00:00:00Z', p50_ms: 1680, p95_ms: 3820, p99_ms: 5540, avg_ms: 1970, trace_count: 119 },
  { timestamp: '2026-03-02T00:00:00Z', p50_ms: 2340, p95_ms: 5680, p99_ms: 7920, avg_ms: 2810, trace_count: 203 },
  { timestamp: '2026-03-03T00:00:00Z', p50_ms: 1890, p95_ms: 4380, p99_ms: 6180, avg_ms: 2210, trace_count: 167 },
  { timestamp: '2026-03-04T00:00:00Z', p50_ms: 1760, p95_ms: 4120, p99_ms: 5960, avg_ms: 2080, trace_count: 134 },
];

/**
 * PerformanceTab - Latency analysis tab content for AppDetails
 *
 * Self-contained component that fetches latency data and renders charts.
 * No layout wrapper or breadcrumbs - rendered inside AppDetails' TabbedPageLayout.
 */
export default function PerformanceTab() {
  const { selectedAppId } = useParams();
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange } = useTimeRange();
  const { session } = useStytchMemberSession();
  const { sessionId, getJwt } = useSessionJwt();

  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tenantId = session?.organization_id || null;
  const inDemoMode = isInDemoMode(tenantId, selectedEnvironment, selectedAppId);

  useEffect(() => {
    if (inDemoMode) {
      setAppData({ latency_over_time: MOCK_LATENCY_DATA });
      setLoading(false);
      return;
    }

    const jwt = getJwt();
    if (!jwt || !selectedAppId || !selectedEnvironment) {
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const params = new URLSearchParams({
      env: selectedEnvironment,
      range: selectedRange,
    });
    const url = `${apiUrl}${v2ServicePath(selectedAppId, '/metrics/latency')}?${params.toString()}`;

    setLoading(true);
    setError(null);

    fetch(url, {
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
    })
      .then(async response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const body = await response.json();
        const data = body?.data || {};
        setAppData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('[PerformanceTab] Error fetching performance data:', err);
        setError(err.message || 'Failed to fetch performance data');
        setLoading(false);
      });
  }, [sessionId, getJwt, selectedAppId, selectedEnvironment, inDemoMode, selectedRange]);

  if (loading) {
    return <div className={components.table.empty}>{LOADING_MESSAGE}</div>;
  }

  if (error && !inDemoMode) {
    return <div className={components.table.empty}><div className="text-danger">{error}</div></div>;
  }

  return (
    <LatencyAnalysisSection
      latencyOverTimeData={appData?.latency_over_time || []}
      latencyDistribution={appData?.latency_distribution || []}
      timeWindow={selectedRange}
      appId={selectedAppId}
      environment={selectedEnvironment}
    />
  );
}
